import React, { useEffect, useState } from "react";
import { sendPOSTRequest } from "../../requestHelper/sendRequest";
import { useTranslation } from "react-i18next";
import {
	FrontendCompanyWithAdditionalInfomation,
	FrontendUserInfo,
} from "node-library/dist/portal";
import {
	FrontendContentPage,
	Severity,
	SystemAddOn,
	SystemRole,
} from "node-library/dist/constants";
import { CustomIconButton } from "../../common-components/button";
import LastPageIcon from "@mui/icons-material/LastPage";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import AddIcon from "@mui/icons-material/Add";

import {
	NeutralColor,
	PrimaryColor,
	SecondaryColor,
} from "node-library/dist/colors";
import { Divider, Tooltip } from "@mui/material";
import SidebarRow from "./sidebarRow";
import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import {
	SidebarInfoIcon,
	SidebarOrderIcon,
	SidebarProfileIcon,
	SidebarRosterIcon,
	SidebarSignoutIcon,
} from "../../common-components/sidebarSvgIcons";

import MenuIcon from "@mui/icons-material/Menu";
import {
	hasPermission,
	PermissionKey,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";
import { doesCompanyHaveAddonsEnabled } from "node-library/dist/helpers/request_helper";

import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	user: FrontendUserInfo | undefined;
	contentPage: FrontendContentPage;
	unsavedChanges: boolean;
	isExpanded: boolean;
	setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
	navigateToContentPage: (page: FrontendContentPage) => void;
	setUnsavedChangesModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setProceedAction: React.Dispatch<React.SetStateAction<() => void>>;
	signOut: () => void;
}

export default function DesktopSidebar(props: Props) {
	const { t: translation } = useTranslation();
	const { isExpanded, setIsExpanded, signOut } = props;

	const { openSnackbar } = useSnackbar();

	const [isMobile, setIsMobile] = useState(false);
	const [mobileAvatarTooltipOpen, setMobileAvatarTooltipOpen] = useState(false);

	// Detect screen width and update `isMobile` state
	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 600); // 600px breakpoint for mobile
		};

		handleResize(); // Check initial screen size
		window.addEventListener("resize", handleResize); // Add resize event listener

		return () => window.removeEventListener("resize", handleResize); // Cleanup event listener
	}, []);

	const user = props.user;

	function gotoPage(page: FrontendContentPage) {
		if (props.contentPage === page) {
			return;
		}
		if (!props.unsavedChanges) {
			props.navigateToContentPage(page);
		} else {
			props.setProceedAction(() => () => {
				props.navigateToContentPage(page);
			});
			props.setUnsavedChangesModalOpen(true);
		}
	}

	const actionRowElements: {
		sectionLabel: string;
		children: {
			label: string;
			icon: JSX.Element;
			page: FrontendContentPage[];
			permissionRequired: PermissionKey[];
			roleRequired?: SystemRole;
			addonRequired: SystemAddOn[];
		}[];
	}[] = [
		...(isMobile
			? [
					{
						sectionLabel: "",
						children: [
							{
								label: translation("ClientPortalOrder"),
								icon: (
									<AddIcon
										sx={{
											color:
												props.contentPage === FrontendContentPage.NEW_ORDER_PAGE
													? PrimaryColor.PRIMARY_LV5
													: NeutralColor.NEUTRAL_GREY_950,
										}}
									></AddIcon>
								),
								page: [FrontendContentPage.NEW_ORDER_PAGE],
								permissionRequired: [Permissions.orders.create],
								addonRequired: [SystemAddOn.CLIENT_PORTAL_INTEGRATION],
							},
						],
					},
				]
			: []),
		{
			sectionLabel: translation("Menu"),
			children: [
				{
					label: translation("Orders"),
					icon: (
						<SidebarOrderIcon
							strokeColor={
								props.contentPage === FrontendContentPage.VIEW_ORDER_PAGE ||
								props.contentPage === FrontendContentPage.ORDER_DETAILS_PAGE
									? PrimaryColor.PRIMARY_LV5
									: NeutralColor.NEUTRAL_GREY_950
							}
							fillColor={
								props.contentPage === FrontendContentPage.VIEW_ORDER_PAGE ||
								props.contentPage === FrontendContentPage.ORDER_DETAILS_PAGE
									? PrimaryColor.PRIMARY_LV5
									: "transparent"
							}
						></SidebarOrderIcon>
					),
					page: [
						FrontendContentPage.VIEW_ORDER_PAGE,
						FrontendContentPage.ORDER_DETAILS_PAGE,
					],
					permissionRequired: [Permissions.orders.list],
					addonRequired: [],
				},
				{
					label: translation("Organization"),
					icon: (
						<SidebarRosterIcon
							strokeColor={
								props.contentPage === FrontendContentPage.CLIENTS_PAGE
									? PrimaryColor.PRIMARY_LV5
									: NeutralColor.NEUTRAL_GREY_950
							}
							fillColor={
								props.contentPage === FrontendContentPage.CLIENTS_PAGE
									? PrimaryColor.PRIMARY_LV5
									: "transparent"
							}
						></SidebarRosterIcon>
					),

					page: [FrontendContentPage.CLIENTS_PAGE],
					permissionRequired: [Permissions.clientOrgs.list],
					addonRequired: [],
				},

				{
					label: translation("Roster"),
					icon: (
						<SidebarRosterIcon
							strokeColor={
								props.contentPage === FrontendContentPage.ROSTER_PAGE
									? PrimaryColor.PRIMARY_LV5
									: NeutralColor.NEUTRAL_GREY_950
							}
							fillColor={
								props.contentPage === FrontendContentPage.ROSTER_PAGE
									? PrimaryColor.PRIMARY_LV5
									: "transparent"
							}
						></SidebarRosterIcon>
					),

					page: [FrontendContentPage.ROSTER_PAGE],
					permissionRequired: [Permissions.users.list],
					addonRequired: [],
				},

				{
					label: translation("Logs"),
					icon: (
						<SidebarRosterIcon
							strokeColor={
								props.contentPage === FrontendContentPage.LOG_PAGE
									? PrimaryColor.PRIMARY_LV5
									: NeutralColor.NEUTRAL_GREY_950
							}
							fillColor={
								props.contentPage === FrontendContentPage.LOG_PAGE
									? PrimaryColor.PRIMARY_LV5
									: "transparent"
							}
						></SidebarRosterIcon>
					),

					page: [FrontendContentPage.LOG_PAGE],
					permissionRequired: [Permissions.logs.search],
					addonRequired: [],
				},
				{
					label: translation("Structure"),
					icon: (
						<SidebarRosterIcon
							strokeColor={
								props.contentPage === FrontendContentPage.LAB_STRUCTURE
									? PrimaryColor.PRIMARY_LV5
									: NeutralColor.NEUTRAL_GREY_950
							}
							fillColor={
								props.contentPage === FrontendContentPage.LAB_STRUCTURE
									? PrimaryColor.PRIMARY_LV5
									: "transparent"
							}
						></SidebarRosterIcon>
					),

					page: [FrontendContentPage.LAB_STRUCTURE],
					permissionRequired: [],
					roleRequired: SystemRole.SYSTEM_ADMIN,
					addonRequired: [],
				},

				{
					label: translation("FilePassword"),
					icon: (
						<SidebarRosterIcon
							strokeColor={
								props.contentPage === FrontendContentPage.FILE_PASSWORD
									? PrimaryColor.PRIMARY_LV5
									: NeutralColor.NEUTRAL_GREY_950
							}
							fillColor={
								props.contentPage === FrontendContentPage.FILE_PASSWORD
									? PrimaryColor.PRIMARY_LV5
									: "transparent"
							}
						></SidebarRosterIcon>
					),

					page: [FrontendContentPage.FILE_PASSWORD],
					permissionRequired: [Permissions.tempPassword.generate],
					addonRequired: [SystemAddOn.TEMP_PASSWORD_MANAGEMENT],
				},
			],
		},
		{
			sectionLabel: translation("Testing"),
			children: [
				{
					label: translation("Instances"),
					icon: (
						<SidebarRosterIcon
							strokeColor={
								props.contentPage === FrontendContentPage.INSTANCE_PAGE ||
								props.contentPage === FrontendContentPage.INSTANCE_DETAILS_PAGE
									? PrimaryColor.PRIMARY_LV5
									: NeutralColor.NEUTRAL_GREY_950
							}
							fillColor={
								props.contentPage === FrontendContentPage.INSTANCE_PAGE ||
								props.contentPage === FrontendContentPage.INSTANCE_DETAILS_PAGE
									? PrimaryColor.PRIMARY_LV5
									: "transparent"
							}
						></SidebarRosterIcon>
					),

					page: [
						FrontendContentPage.INSTANCE_PAGE,
						FrontendContentPage.INSTANCE_DETAILS_PAGE,
					],
					permissionRequired: [
						Permissions.sampleInstances.adminList,
						Permissions.sampleInstances.analystList,
					],
					addonRequired: [],
				},

				{
					label: translation("Batches"),
					icon: (
						<SidebarRosterIcon
							strokeColor={
								props.contentPage === FrontendContentPage.BATCH_PAGE ||
								props.contentPage === FrontendContentPage.BATCH_DETAILS_PAGE
									? PrimaryColor.PRIMARY_LV5
									: NeutralColor.NEUTRAL_GREY_950
							}
							fillColor={
								props.contentPage === FrontendContentPage.BATCH_PAGE ||
								props.contentPage === FrontendContentPage.BATCH_DETAILS_PAGE
									? PrimaryColor.PRIMARY_LV5
									: "transparent"
							}
						></SidebarRosterIcon>
					),

					page: [
						FrontendContentPage.BATCH_PAGE,
						FrontendContentPage.BATCH_DETAILS_PAGE,
					],
					permissionRequired: [Permissions.batches.listOverview],
					addonRequired: [],
				},
				{
					label: translation("BatchGeneration"),
					icon: (
						<SidebarRosterIcon
							strokeColor={
								props.contentPage === FrontendContentPage.BATCH_GENERATION_PAGE
									? PrimaryColor.PRIMARY_LV5
									: NeutralColor.NEUTRAL_GREY_950
							}
							fillColor={
								props.contentPage === FrontendContentPage.BATCH_GENERATION_PAGE
									? PrimaryColor.PRIMARY_LV5
									: "transparent"
							}
						></SidebarRosterIcon>
					),

					page: [FrontendContentPage.BATCH_GENERATION_PAGE],
					permissionRequired: [
						Permissions.batches.adminGenerate,
						Permissions.batches.analystGenerate,
					],
					addonRequired: [],
				},
			],
		},
		{
			sectionLabel: translation("Account"),
			children: [
				{
					label: translation("Profile"),
					icon: (
						<SidebarProfileIcon
							strokeColor={
								props.contentPage === FrontendContentPage.USER_INFO_PAGE
									? PrimaryColor.PRIMARY_LV5
									: NeutralColor.NEUTRAL_GREY_950
							}
							fillColor={
								props.contentPage === FrontendContentPage.USER_INFO_PAGE
									? PrimaryColor.PRIMARY_LV5
									: "transparent"
							}
						></SidebarProfileIcon>
					),

					page: [FrontendContentPage.USER_INFO_PAGE],
					permissionRequired: [],
					addonRequired: [],
				},
			],
		},
	];
	const footerRowElements = [
		{
			label: translation("Logout"),
			icon: <SidebarSignoutIcon></SidebarSignoutIcon>,

			onClick: signOut,
		},
	];

	const avatarPlaceholder: React.ReactNode = (
		<div
			className={`typography-body-01`}
			style={{ color: SecondaryColor.SECONDARY_200 }}
		>{`${user?.firstName.charAt(0)}${user?.lastName.charAt(0)}`}</div>
	);
	return (
		<div
			className={`landing-page-sidebar ${
				isExpanded ? `` : `sidebar-collapsed`
			}`}
		>
			<div className="sidebar-header">
				{/* <div
					className={`ppb-logo sidebar-ppb-logo ${
						isExpanded || isMobile ? `` : `sidebar-collapse-hide`
					}`}
				></div> */}
				<div style={{ display: "flex", gap: "5px" }}>
					{isMobile ? (
						<>
							<Tooltip
								PopperProps={{
									sx: {
										"& .MuiTooltip-tooltip": {
											backgroundColor: NeutralColor.NEUTRAL_GREY_950,
											padding: "12px 16px",
										},
									},
								}}
								title={`${user?.firstName} ${user?.lastName}`}
								arrow
								open={mobileAvatarTooltipOpen} // Control the tooltip's visibility with state
								onClose={() => setMobileAvatarTooltipOpen(false)} // Close tooltip when clicking outside
							>
								<div
									className="sidebar-user-avatar"
									onClick={() => {
										setMobileAvatarTooltipOpen(!mobileAvatarTooltipOpen);
									}}
								>
									{avatarPlaceholder}
								</div>
							</Tooltip>

							<CustomIconButton
								id={"sidebar-expand-toggle-button"}
								icon={<MenuIcon />}
								onClick={() => {
									setIsExpanded(!isExpanded);
								}}
							/>
						</>
					) : (
						<CustomIconButton
							id={"sidebar-expand-toggle-button"}
							icon={
								isExpanded ? <FirstPageIcon></FirstPageIcon> : <LastPageIcon />
							}
							onClick={() => {
								setIsExpanded(!isExpanded);
							}}
						/>
					)}
				</div>
			</div>

			{!isMobile && (
				<div
					className={`sidebar-user-info ${
						isExpanded ? `` : `sidebar-user-info-collapsed`
					}`}
				>
					<div className="sidebar-user-avatar">{avatarPlaceholder}</div>
					<div className="sidebar-username-email-container">
						<div
							className={`typography-body-02  ${
								isExpanded ? `` : `sidebar-collapse-hide`
							}`}
							style={{ wordWrap: "break-word" }}
						>{`${user?.firstName} ${user?.lastName}`}</div>
					</div>
				</div>
			)}

			{!isMobile &&
				user?.role &&
				hasPermission(user?.role?.permissions, Permissions.orders.create) && (
					<div
						className={`new-order-button ${
							props.contentPage === FrontendContentPage.NEW_ORDER_PAGE
								? `new-order-button-selected`
								: ``
						}`}
						onClick={() => {
							gotoPage(FrontendContentPage.NEW_ORDER_PAGE);
						}}
					>
						<AddIcon />
						<span
							className={`typography-body-02  ${
								isExpanded ? `` : `sidebar-collapse-hide`
							}`}
						>
							{translation("ClientPortalOrder")}
						</span>
					</div>
				)}

			<div style={{ overflowY: "auto" }}>
				{actionRowElements.map((element, index) => {
					// Only keep children the user has access to
					const visibleChildren = element.children.filter((childRow: any) => {
						//check addon first
						if (
							!doesCompanyHaveAddonsEnabled(
								props.company,
								childRow.addonRequired,
							)
						) {
							return false;
						}
						// If a role is required, the user must have exactly that role
						if (
							childRow.roleRequired &&
							user?.role?.roleKey !== childRow.roleRequired
						) {
							return false;
						}

						if (
							!childRow.permissionRequired ||
							childRow.permissionRequired.length == 0
						) {
							return true;
						}
						return (
							user &&
							user.role &&
							user.role.permissions &&
							childRow.permissionRequired.some((permission: PermissionKey) => {
								return hasPermission(
									user.role?.permissions as PermissionMap,
									permission,
								);
							})
						);
					});

					// If no visible children, skip rendering this section
					if (visibleChildren.length === 0) return null;

					const rowItems = visibleChildren.map((childRow: any, i: number) => (
						<SidebarRow
							key={i}
							label={childRow.label}
							icon={childRow.icon}
							isExpanded={isExpanded}
							isMobile={isMobile}
							selected={childRow.page.includes(props.contentPage)}
							onClick={() => gotoPage(childRow.page[0])}
						/>
					));

					return (
						<div key={index}>
							<Divider
								className="sidebar-section-divider"
								orientation="horizontal"
								sx={{ my: 1 }}
								flexItem
							/>

							<div
								className={`typography-caption sidebar-section-label ${
									isExpanded ? `` : `sidebar-collapse-hide`
								}`}
								style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
							>
								{element.sectionLabel}
							</div>

							<div className="sidebar-action-row-container">{rowItems}</div>
						</div>
					);
				})}
			</div>
			{(!isMobile || isExpanded) && (
				<div className="sidebar-footer">
					<Divider orientation="horizontal" sx={{ my: 1 }} flexItem />
					{footerRowElements.map((childRow, index) => {
						return (
							<SidebarRow
								key={index}
								label={childRow.label}
								icon={childRow.icon}
								isExpanded={isExpanded}
								selected={
									// childRow.page ? props.contentPage === childRow.page : false
									false
								}
								isMobile={isMobile}
								onClick={childRow.onClick}
							></SidebarRow>
						);
					})}
				</div>
			)}
		</div>
	);
}
